<template>
    <div>
        <MAModal
            v-if="store__showCreateModal"
            id="dashboard-modal"
            title="New Data Source"
            @close="store__toggleCreateModal()"
        >
            <!-- Step 1 -->
            <div
                v-if="!step1Complete"
                slot="content"
                class="data-uploader-modal"
            >
                <div class="slds-setup-assistant">
                    <h3 class="slds-setup-assistant__step-summary-title slds-text-heading_small">
                        Data Source Description
                    </h3>
                    <div slot="description">
                        <div class="slds-form-element slds-m-bottom_medium">
                            <MAInput
                                v-model="store__dataset.datasetLabel"
                                :isRequired="true"
                                label="Name"
                                class="slds-form-element"
                            />
                        </div>
                        <div class="slds-form-element slds-m-bottom_medium">
                            <MAInput
                                v-model="store__dataset.datasetDescription"
                                :isRequired="false"
                                label="Description"
                                class="slds-form-element"
                            />
                        </div>
                        <h3 class="slds-setup-assistant__step-summary-title slds-text-heading_small">
                            Import Data Source
                        </h3>
                        <div>
                            Upload CSV
                        </div>
                        <div>
                            <FileReader v-if="store__loginState.isLoggedIn" />
                            <MASpinner
                                v-if="store__isUploading"
                                size="medium"
                                theme="brand"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <!-- End Step 1 -->

            <!-- Step 2 -->
            <div
                v-if="step1Complete && !step2Complete"
                slot="content"
                class="data-uploader-modal"
            >
                <MASpinner v-if="store__isUploading" />
                <ol class="slds-setup-assistant">
                    <h3 class="slds-setup-assistant__step-summary-title slds-text-heading_small">
                        Select Your Location Headers
                    </h3>
                    <div slot="description">
                        <SpatialReferenceCreate />
                    </div>
                </ol>
            </div>
            <!-- End Step 2 -->

            <!-- Step 3 -->
            <div
                v-if="step2Complete && !step3Complete"
                slot="content"
                class="data-uploader-modal"
            >
                <MASpinner v-if="store__isUploading" />
                <ol class="slds-setup-assistant">
                    <h3 class="slds-setup-assistant__step-summary-title slds-text-heading_small">
                        Set Up Your Column Headers
                    </h3>
                    <div slot="description">
                        <DataColumnsCreate :key="componentKey" />
                    </div>
                </ol>
            </div>
            <!-- End Step 3-->

            <!-- Step 1 Footer-->
            <div
                v-if="!step1Complete"
                slot="footer"
                class="slds-grid slds-grid_align-end"
            >
                <button
                    class="slds-button slds-button_brand"
                    :disabled="store__isUploading"
                    @click="checkDescriptionsInputs()"
                >
                    Next
                </button>
            </div>
            <!-- End Step 1 Footer-->

            <!-- Step 2 Footer-->
            <div
                v-if="step1Complete && !step2Complete"
                slot="footer"
                class="slds-grid slds-grid_align-spread"
            >
                <button
                    class="slds-button slds-button_neutral"
                    :disabled="store__lockButtons"
                    @click="toggleStep2()"
                >
                    Previous
                </button>
                <button
                    class="slds-button slds-button_brand"
                    :disabled=" store__lockButtons"
                    @click="checkSpatialReferenceSelection()"
                >
                    Next
                </button>
            </div>
            <!-- End Step 2 Footer-->

            <!-- Step 3 Footer-->
            <div
                v-if="step2Complete && !step3Complete"
                slot="footer"
                class="slds-grid slds-grid_align-spread"
            >
                <button
                    class="slds-button slds-button_neutral"
                    :disabled="store__lockButtons"
                    @click="toggleStep3()"
                >
                    Previous
                </button>
                <button
                    class="slds-button slds-button_brand"
                    :disabled="!store__spatialReferenceSelectionDone || store__lockButtons"
                    @click="uploadSchema()"
                >
                    Save and Process
                </button>
            </div>
            <!-- End Step 3 Footer-->
        </MAModal>
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import MAModal from '@mapanything/ma-lib/src/vue/components/MAModal.vue';
import MAInput from '@mapanything/ma-lib/src/vue/components/MAInput.vue';
import MASpinner from '@mapanything/ma-lib/src/vue/components/MASpinner.vue';

import FileReader from './FileReader.vue';
import SpatialReferenceCreate from './SpatialReferenceCreate.vue';
import DataColumnsCreate from './DataColumnsCreate.vue';

export default {
    name: 'Dashboard',
    components: {
        MAModal,
        MAInput,
        MASpinner,
        FileReader,
        SpatialReferenceCreate,
        DataColumnsCreate
    },
    data() {
        return {
            step1Complete: false,
            step2Complete: false,
            step3Complete: false,
            schemaConfigComplete: false,
            schemaConfigValidated: false,
            idLabelIndex: null,
            datasets: []
        };
    },
    computed: {
        ...mapState({
            store__loginState: 'loginState',
            store__schema: 'schema',
            store__dataset: 'dataset',
            store__spatialReferencePickerVisible: 'spatialReferencePickerVisible',
            store__spatialReferenceSelectionDone: 'spatialReferenceSelectionDone',
            store__spatialValuesIndex: 'spatialValuesIndex',
            store__lockButtons: 'lockButtons',
            store__showCreateModal: 'showCreateModal',
            store__toast: 'toast',
            store__spatialReference: 'spatialReference',
            store__isUploading: 'isUploading'
        })
    },
    mounted() {
        this.resetForm();
    },
    methods: {
        ...mapMutations({
            store__showSpatialReferencePicker: 'showSpatialReferencePicker',
            store__markSpatialReferenceAsDone: 'markSpatialReferenceAsDone',
            store__toggleCreateModal: 'toggleCreateModal',
            store__toggleUploadSpinner: 'toggleUploadSpinner',
            store__toggleLockButtons: 'toggleLockButtons',
            store__resetData: 'resetData',
            store__resetSpatialValuesIndex: 'resetSpatialValuesIndex'
        }),
        /* Resets the label, description, and all schema inputs */
        resetForm() {
            // Reset schema data...
            this.store__resetData(true);
            // Reset spatial values index...
            this.store__resetSpatialValuesIndex();
            // Reset dataset...
            this.datasets = [];
            // Reset visibility of portions of modal...
            this.step1Complete = false;
            this.schemaConfigComplete = false;
            this.schemaConfigValidated = false;
            this.store__showSpatialReferencePicker(false);
            this.store__markSpatialReferenceAsDone(false);
            // Append the current user's org ID by default...
            this.store__dataset.organizationIds += this.store__loginState.orgId;
        },
        /* Checks that all required input fields are filled */
        checkDescriptionsInputs() {
            if (this.store__dataset.datasetLabel === '') {
                const toastMessage = {
                    showCloseButton: true,
                    type: 'error',
                    title: 'Missing Required Inputs',
                    body: 'Dataset Name is required',
                    timeout: 5000
                };

                this.$vueOnToast.pop(toastMessage);
            } else if (!this.store__dataset.showFileUploadSuccess) {
                const toastMessage = {
                    showCloseButton: true,
                    type: 'error',
                    title: 'Missing CSV',
                    body: 'CSV Upload Required',
                    timeout: 5000
                };

                this.$vueOnToast.pop(toastMessage);
            } else {
                this.toggleStep2();
            }
        },
        checkSpatialReferenceSelection() {
            const spatialIndex = this.store__spatialReference;
            if (spatialIndex.name.index != null) {
                // Check if Lat/Lng are selected...
                if (spatialIndex.latitude.index != null && spatialIndex.longitude.index != null) {
                    this.store__markSpatialReferenceAsDone(true);
                    this.toggleStep3();
                // Or any street address fields...
                } else if (spatialIndex.street.index != null
                        || spatialIndex.city.index != null
                        || spatialIndex.state.index != null
                        || spatialIndex.country.index != null
                        || spatialIndex.zip.index != null) {
                    this.store__markSpatialReferenceAsDone(true);
                    this.toggleStep3();
                } else {
                    const toastMessage = {
                        showCloseButton: true,
                        type: 'error',
                        title: 'Check Location Headers',
                        body: 'You must select at least a Latitude and Longitude OR an address field!',
                        timeout: 5000
                    };

                    this.$vueOnToast.pop(toastMessage);
                }
            } else {
                const toastMessage = {
                    showCloseButton: true,
                    type: 'error',
                    title: 'Name is required',
                    body: 'Select a column to represent location names.',
                    timeout: 5000
                };

                this.$vueOnToast.pop(toastMessage);
            }
        },
        /* Toggles step1Complete state */
        toggleStep2() {
            this.step1Complete = !this.step1Complete;
        },
        toggleStep3() {
            this.step2Complete = !this.step2Complete;
        },
        /*  Posts schema to API */
        uploadSchema() {
            const schema = this.store__schema;
            const visibleFields = schema.filter((column) => column.active === true);

            // Allow only a maximum of 20 cols.
            if (visibleFields.length <= 20) {
                // Begin loading spinner...
                this.store__toggleUploadSpinner();
                // Lock save button...
                this.store__toggleLockButtons();
                // Hide Schema Selection
                this.store__markSpatialReferenceAsDone(false);

                this.postSchema()
                .then(() => {
                    const toastMessage = {
                        showCloseButton: true,
                        type: 'success',
                        title: `"${this.store__dataset.datasetLabel}" is processing`,
                        body: 'You\'ll receive an email when it\'s ready for use.',
                        timeout: 5000
                    };

                    this.$vueOnToast.pop(toastMessage);

                    // Hide modal to show dashboard...
                    this.store__toggleCreateModal();
                    this.schemaConfigComplete = !this.schemaConfigComplete;
                })
                .catch((err) => {
                    let toastBody;

                    if (err.data && err.data.error && err.data.error.message) {
                        toastBody = err.data.error.message;
                    } else {
                        toastBody = 'An error has ocurred while contacting the server for processing. Please contact support.';
                    }

                    const toastMessage = {
                        showCloseButton: true,
                        type: 'error',
                        title: 'Server Error',
                        body: toastBody,
                        timeout: 5000
                    };

                    this.$vueOnToast.pop(toastMessage);

                    throw err;
                })
                .finally(() => {
                    // End loading spinner...
                    this.store__toggleUploadSpinner();
                    // Hide Schema Selection
                    this.store__markSpatialReferenceAsDone(true);
                    // Unlock save button...
                    this.store__toggleLockButtons();
                });
            } else {
                const toastMessage = {
                    showCloseButton: true,
                    type: 'error',
                    title: 'Reduce Visible Fields',
                    body: 'You have too many fields marked visible. Maximum is 20.',
                    timeout: 5000
                };

                this.$vueOnToast.pop(toastMessage);
            }
        },
        /* POST request to test schema */
        postSchema() {
            return new Promise((resolve, reject) => {
                const API_URL = document.location.href;

                const postBody = {
                    datasetID: this.store__dataset.datasetID,
                    datasetLabel: this.store__dataset.datasetLabel,
                    datasetDescription: (this.store__dataset.datasetDescription.trim() === '') ? null : this.store__dataset.datasetDescription,
                    parserOptions: {
                        type: 'csv',
                        escapeCharacter: '"',
                        quoteCharacter: '"',
                        hasHeader: true
                    },
                    columnFormat: this.prepareSchema(this.store__schema),
                    idLabelIndex: this.idLabelIndex
                };

                this.$http.post(`${API_URL}api/schema`, postBody)
                .then((res) => {
                    if (res.status === 200) {
                        resolve(res);
                    } else {
                        console.log('Unknown client error. Please contact support.');
                        reject(res);
                    }
                })
                .catch((err) => {
                    if (err.response.status === 500) {
                        err.response.data.error = { message: 'An internal server error has ocurred. Please contact support.' };
                    }
                    reject(err.response);
                });
            });
        },
        prepareSchema(schema) {
            const cleanSchema = [];

            schema.forEach((column) => {
                if (column.spatial) {
                    if (column.spatial === 'name') {
                        this.idLabelIndex = column.index;
                        cleanSchema.push({
                            index: column.index,
                            label: column.renamedLabel,
                            format: column.format,
                            active: column.active
                        });
                    } else {
                        cleanSchema.push({
                            index: column.index,
                            label: column.renamedLabel,
                            spatial: column.spatial
                        });
                    }
                } else {
                    cleanSchema.push({
                        index: column.index,
                        label: column.renamedLabel,
                        format: column.format,
                        active: column.active
                    });
                }
            });

            return cleanSchema;
        }
    }
};
</script>

<style lang="scss" scoped>
    .slds-pill__icon_container .slds-icon {
        display: flex;
        justify-content: center;
    }

    .slds-setup-assistant__item {
        border-bottom: none;
    }

    .dashboard-modal {
        min-height: 70%;
    }

    .hide-close /deep/ header button {
        display: none
    }

    .ma-icon-page {
        font-size: 2.5rem;
    }

    .ma-icon-success, .ma-icon-page {
        color: #4bca81;
        display: flex;
    }

    .slds-progress-bar--large, .slds-progress-bar_large {
        height: .75rem;
    }
</style>
